import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { isHubSession, sessionEndedPath } from '../utils/hubSession';
import { useAuth } from '../contexts/AuthContext';
import { usePlatformBranding } from '../contexts/PlatformBrandingContext';
import PlatformLogo from '../components/layout/PlatformLogo';
import apiClient from '../api/client';
import { toast } from 'react-toastify';
import { LogIn, Eye, EyeOff } from 'lucide-react';


const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const platform = usePlatformBranding();
  // This BMS's name as registered on the platform (the backend reads it from the hub)
  const [bmsName, setBmsName] = useState('');
  useEffect(() => {
    let live = true;
    apiClient
      .get('/platform/identity')
      .then((res) => live && setBmsName(res.data?.data?.bmsName || ''))
      .catch(() => undefined); // not shown until the BMS can reach the hub
    return () => {
      live = false;
    };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  // Get intended destination - but we might override based on role
  const intendedDestination = location.state?.from?.pathname;

  // People who came from One Manager have no password here: they see the session-ended screen,
  // with a button back to One Manager. This app's own accounts on the same device still reach the
  // form with /login?local=1.
  const toHub = isHubSession() && !new URLSearchParams(location.search).has('local');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear any previous error

    // Basic validation
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // Check if password change is required
        if (result.requirePasswordChange) {
          navigate('/change-password', { replace: true, state: { firstLogin: true } });
        } else {
          // The module selection page sends them on: their role's permissions and the plan decide
          // the modules, with a choice when there is more than one. A page they were trying to
          // open is honoured (its own guard still checks their permissions).
          const finalRedirect =
            intendedDestination && intendedDestination !== '/dashboard'
              ? intendedDestination
              : '/select-module';

          navigate(finalRedirect, { replace: true });
        }
      } else {
        // Login failed - show error message
        setError(result.error || 'Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error('Login submit error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  if (toHub) return <Navigate to={sessionEndedPath('no-session')} replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-black via-primary-charcoal to-primary-slate flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* The platform's logo and name, and this BMS's name as the platform registered it (hub contract §3.1) */}
        <div className="text-center mb-8">
          <h1 className="flex items-center justify-center gap-3">
            <PlatformLogo size="lg" tone="dark" />
            {bmsName && (
              <span className="rounded-md bg-accent-gold/20 px-2 py-0.5 text-sm font-semibold text-accent-goldLight">
                {bmsName}
              </span>
            )}
          </h1>
          <p className="mt-2 text-gray-300">Business Management System</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-primary-black mb-6 text-center">Sign In</h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent transition-colors"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent transition-colors"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-primary-black border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <a
              href="/forgot-password"
              className="text-sm text-accent-gold hover:text-accent-goldDark transition-colors"
            >
              Forgot your password?
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          © {new Date().getFullYear()} {platform.name}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
